import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BehaviorSubject,
  Subject,
  startWith,
  take,
  tap,
  catchError,
  of,
} from 'rxjs';
import { FeathersService } from '../../../../core/services/api/feathers.service';
import { FilesService } from '../../../../core/services/api/files.service';
import { ApiEndpoint } from '../../../../core/enums';
import { LingFileEntity } from '../../../../core/models/ling-file-entity';
import { environment } from '../../../../../environments/environment';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';

const PAGE_SIZE = 40;
const SCROLL_LOAD_THRESHOLD = 0.8;

@Component({
  selector: 'ling-add-file-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
  templateUrl: './add-file-dialog.component.html',
  styleUrls: ['./add-file-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddFileDialogComponent {
  private readonly feathers = inject(FeathersService);
  private readonly fileService = inject(FilesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogRef = inject(MatDialogRef<AddFileDialogComponent>);

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('scrollContainer') scrollContainerRef!: ElementRef<HTMLElement>;

  protected readonly previewErrors = new Set<string>();
  protected readonly reloadMedia$ = new Subject<void>();
  protected readonly accumulatedItems$ = new BehaviorSubject<LingFileEntity[]>(
    []
  );
  protected readonly totalCount$ = new BehaviorSubject(0);
  protected readonly isLoadingMore$ = new BehaviorSubject(false);

  protected readonly media$ = this.accumulatedItems$.asObservable();

  constructor() {
    this.reloadMedia$
      .pipe(
        startWith(undefined),
        tap(() => {
          this.accumulatedItems$.next([]);
          this.totalCount$.next(0);
          this.loadPage(0);
        })
      )
      .subscribe();
  }

  private loadPage(page: number): void {
    if (this.isLoadingMore$.value) return;
    this.isLoadingMore$.next(true);
    this.cdr.markForCheck();
    const creator = this.feathers.me$.value?._id;
    const query: Record<string, unknown> = {
      $limit: PAGE_SIZE,
      $skip: page * PAGE_SIZE,
      ...(creator && { creator }),
    };
    this.feathers
      .watchSearch<LingFileEntity>(ApiEndpoint.Media, { query })
      .pipe(
        take(1),
        tap((res) => {
          const data = res?.data ?? [];
          const total = res?.total ?? data.length;
          const prev = this.accumulatedItems$.value;
          const isFirst = page === 0;
          this.accumulatedItems$.next(isFirst ? data : [...prev, ...data]);
          this.totalCount$.next(total);
          this.isLoadingMore$.next(false);
          this.cdr.markForCheck();
        }),
        catchError(() => {
          this.isLoadingMore$.next(false);
          this.cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  protected onScroll(): void {
    const el = this.scrollContainerRef?.nativeElement;
    if (!el || this.isLoadingMore$.value) return;
    const total = this.totalCount$.value;
    const loaded = this.accumulatedItems$.value.length;
    if (loaded >= total) return;
    const scrollRatio =
      (el.scrollTop + el.clientHeight) / (el.scrollHeight || 1);
    if (scrollRatio >= SCROLL_LOAD_THRESHOLD) {
      const nextPage = Math.floor(loaded / PAGE_SIZE);
      this.loadPage(nextPage);
    }
  }

  protected getMediaUrl(item: LingFileEntity): string {
    const filename = item.file?.filename;
    return filename ? `${environment.url}/files/${filename}` : '';
  }

  protected isImage(item: LingFileEntity): boolean {
    return (item.file?.mimetype ?? '').startsWith('image/');
  }

  protected isPreviewError(item: LingFileEntity): boolean {
    return !item._id || this.previewErrors.has(item._id);
  }

  protected setPreviewError(item: LingFileEntity): void {
    if (item._id) {
      this.previewErrors.add(item._id);
      this.cdr.markForCheck();
    }
  }

  protected onUploadClick(): void {
    this.fileInputRef?.nativeElement?.click();
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) return;
    this.fileService
      .uploadMedia(Array.from(files))
      .pipe(
        tap((uploaded) => {
          if (uploaded?.length) {
            this.dialogRef.close(uploaded[0]);
          } else {
            this.reloadMedia$.next();
          }
        })
      )
      .subscribe();
    input.value = '';
  }

  protected selectFile(item: LingFileEntity): void {
    this.dialogRef.close(item);
  }
}
